import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { adminApi } from "../../../../../../../../../../../../../../../services/Api/Admin/adminApi";
import {
	CrashReportApi,
	CrashReportFormApi,
} from "../../../../../../../../../../../../../../../services/Api/Admin/adminTypes";
import CrashReportButtons from "./CrashReportButtons";

interface Props {
	crashReport: CrashReportApi;
	handleSuccessAction: (crashReport: CrashReportApi) => void;
	handleCloseCrashReport: () => void;
	handleRestoreCrashReport: () => void;
	handleStartFixingCrashReport: () => void;
	setShowMarkAsPermanentlyBrokenModal: (value: boolean) => void;
}

const CrashReportForm = ({
	crashReport,
	handleSuccessAction,
	handleRestoreCrashReport,
	handleStartFixingCrashReport,
	handleCloseCrashReport,
	setShowMarkAsPermanentlyBrokenModal,
}: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<CrashReportFormApi>({
		defaultValues: { comment: crashReport?.comment, fixed_version_code: crashReport?.fixed_version_code },
		criteriaMode: "all",
	});
	const {
		formState: { errors },
		setError,
		register,
		formState,
		handleSubmit,
		reset,
		control,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			if (crashReport) {
				const params: Record<string, any> = { include: "occurences" };
				const res = await adminApi.updateCrashReport(crashReport, { ...data }, params);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				reset(res);
				handleSuccessAction(res);
			}
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} onSubmit={onSubmit}>
			<FormInput
				as={"textarea"}
				rows={3}
				label={t("lib:common.word.comment")}
				register={register}
				name="comment"
				errors={errors}
			/>
			<FormMoneyInput
				currency={"PLN"}
				decimalScale={0}
				suffix=""
				label={t("modules.crash_report.field.fixed_version_code.title")}
				control={control}
				name="fixed_version_code"
				errors={errors}
			/>
			<div className="d-flex gap-2 my-3 flex-wrap">
				<ButtonLoading loading={loading} variant="primary" type="submit">
					{t("common.action.save", { ns: "lib" })}
				</ButtonLoading>
				{formState.isDirty && Object.keys(formState.dirtyFields).length > 0 && window.history.length > 1 && (
					<Button variant="light" type="button" onClick={() => history.push(`/admin/crash_reports`)}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				)}
				<CrashReportButtons
					crashReport={crashReport}
					handleRestoreCrashReport={handleRestoreCrashReport}
					handleCloseCrashReport={handleCloseCrashReport}
					setShowMarkAsPermanentlyBrokenModal={setShowMarkAsPermanentlyBrokenModal}
					handleStartFixingCrashReport={handleStartFixingCrashReport}
				/>
			</div>
		</FormDirty>
	);
};

export default CrashReportForm;
